import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import BackButton from "../../../components/BackButton";
import TimeAndSeat from "./TimeAndSeat";
import CheckoutRequired from "./CheckoutRequired";
import "../../../styles/GetTicket.scss";
import { FaCalendar } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { IoPerson } from "react-icons/io5";
import { tryFetch, IResponse } from "../../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../../helpers/Storage";
import { useEventRating } from "../../hooks/useEventRating";

const CalendarIcon =
  FaCalendar as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const LocationIcon =
  FaLocationDot as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const PersonIcon =
  IoPerson as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

type Customer = {
  name: string;
  email: string;
  phone: string;
  birthdate: string;
};

const GetTicketRequired: React.FC = () => {
  // const { id } = useParams();
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = location.state?.eventId;

  const [eventData, setEventData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedTicketType, setSelectedTicketType] = useState<string | null>(
    null
  );

  const [customers, setCustomers] = useState<Customer[]>([
    { name: "", email: "", phone: "", birthdate: "" },
  ]);

  const formattedRating = useEventRating(eventData?.event_rating || "");

  useEffect(() => {
    const fetchEvent = async () => {
      const res: IResponse = await tryFetch({
        url: `/events/${eventId}`,
        method: "GET",
      });

      if (res.is_success && res.data) {
      const foundEvent = res.data as any;

      const mappedEvent = {
        id: foundEvent.id,
        event_title: foundEvent.event_title,
        venue: foundEvent.venue,
        event_rating: foundEvent.event_rating,
        description: foundEvent.description,
        organizer: foundEvent.organizer,
        dates: foundEvent.all_dates || [],
        tickets: foundEvent.tickets.map((t: any) => ({
          id: t.id,
          ticket_type: t.name,
          price: t.ticket_price,
          available_slot: t.capacity.toString(),
          seats: [],
        })),
        house_rules: foundEvent.house_rules || [],
      };

      setEventData(mappedEvent);
    }
    };

    fetchEvent();
  }, [slug]);

  const handleAddTicket = () => {
    setCustomers((prev) => [
      ...prev,
      { name: "", email: "", phone: "", birthdate: "" },
    ]);
  };

  const handleRemoveTicket = (index: number) => {
    setCustomers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof Customer,
    value: string
  ) => {
    setCustomers((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const allCustomersValid = customers.every(
    (c) => c.name && c.email && c.phone && c.birthdate
  );

  // const handleProceed = async (ticketId: string) => {
  const handleProceed = async (ticketId: string | undefined) => {
  if (!ticketId) {
    alert("Please select a ticket type.");
    return;
  }
    const token = getTokenOrDefault();

    const payload = {
      ticket_type_id: ticketId,
      ticket_count: customers.length.toString(),
      selected_dates: [selectedDate],
      customers,
    };

    console.log("Payload:", {
      ticket_type_id: ticketId,
      ticket_count: customers.length.toString(),
      selected_dates: [selectedDate],
      customers,
    });

    try {
      const res: IResponse = await tryFetch({
        url: `/sec/events/${eventData.id}/tickets/${ticketId}`,
        method: "POST",
        token,
        payload,
      });

      if (res.is_success) {
        const createdId = (res.data as any)?.id || ticketId;
        navigate(`/preview/${createdId}`);
      } else {
        alert(res.error);
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong.");
    }
  };

  if (!eventData) return <div>Loading...</div>;

  const ticketSelected = eventData.tickets.find(
    (t: any) => t.ticket_type.toLowerCase() === selectedTicketType?.toLowerCase()
  );

  return (
    <div className="get-ticket">
      <BackButton />

      <h1 className="page-title">Get Tickets</h1>
      <hr className="divider" />

      <div className="event-details">
        <h2>{eventData.event_title}</h2>
        <p className="e-organizer">by {eventData.organizer}</p>

        <p>
          <CalendarIcon className="icon" />{" "}
          {eventData.dates
            .map((d: string) =>
              new Date(d).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            )
            .join(", ")}
        </p>

        <p>
          <LocationIcon className="icon" /> {eventData.venue}
        </p>

        <p>
          <PersonIcon className="icon" /> {formattedRating}
        </p>
      </div>

      <TimeAndSeat
        tickets={eventData.tickets}
        availableDates={eventData.dates}
        onDateSelect={setSelectedDate}
        onTimeSelect={setSelectedTime}
        onTicketSelect={setSelectedTicketType}
      />

      <div className="customer-forms">
        {customers.map((customer, index) => (
          <div key={index} className="customer-form-container">
            <div className="ticket-label">x{index + 1} Ticket</div>

            <input
              type="text"
              placeholder="Name"
              value={customer.name}
              onChange={(e) => handleChange(index, "name", e.target.value)}
            />

            <input
              type="email"
              placeholder="Email Address"
              value={customer.email}
              onChange={(e) => handleChange(index, "email", e.target.value)}
            />

            <input
              type="text"
              placeholder="Phone Number"
              value={customer.phone}
              onChange={(e) => handleChange(index, "phone", e.target.value)}
            />

            <input
              type="date"
              placeholder="Birthdate"
              value={customer.birthdate}
              onChange={(e) => handleChange(index, "birthdate", e.target.value)}
            />

            {customers.length > 1 && (
              <button
                className="remove-ticket"
                onClick={() => handleRemoveTicket(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <div className="add-ticket-section">
        <button className="add-ticket" onClick={handleAddTicket}>
          + Get more Ticket
        </button>
        </div>
      </div>

      <CheckoutRequired
        ticket={ticketSelected}
        ticketCount={customers.length}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        allCustomersValid={allCustomersValid}
        onProceed={handleProceed}
      />
    </div>
  );
};

export default GetTicketRequired;
